import { bbxKind } from '../agents/bbx-kind.js';
import type { OcrExtractionType } from '../agents/extraction-modules/ocrExtractionTypes.js';
import { artBehaviour } from './art.js';
import { calculationBehaviour } from './calculation.js';
import { chemicalFormulaBehaviour } from './chemical-formula.js';
import { curveAnalysisBehaviour } from './curve-analysis.js';
import { defaultBehaviour } from './default.js';
import { diagramBehaviour } from './diagram.js';
import { dictationBehaviour } from './dictation.js';
import { essayBehaviour } from './essay.js';
import { gapTextBehaviour } from './gap-text.js';
import { geometryBehaviour } from './geometry.js';
import { imageAnalysisBehaviour } from './image-analysis.js';
import { labelImageBehaviour } from './label-image.js';
import { legalCaseBehaviour } from './legal-case.js';
import { mappingBehaviour } from './mapping.js';
import { materialBasedEssayBehaviour } from './material-based-essay.js';
import { mathematicalProofBehaviour } from './mathematical-proof.js';
import { multipleChoiceBehaviour } from './multiple-choice.js';
import { musicBehaviour } from './music.js';
import { nonFictionBehaviour } from './non-fiction.js';
import { plottingBehaviour } from './plotting.js';
import { poetryAnalysisBehaviour } from './poetry-analysis.js';
import { programmingBehaviour } from './programming.js';
import { textBehaviour } from './short-text.js';
import { sketchBehaviour } from './sketch.js';
import { taskFrameBehaviour } from './task-frame.js';
import { translationBehaviour } from './translation.js';

export enum TaskType {
  // BaseQL's registerEnum requires KEY === VALUE for every enum member, so the value cannot stay 'GROUPING'
  // — old exams persisted with taskType: 'GROUPING' need a read-time normalization shim (see below).
  TASK_FRAME = 'TASK_FRAME', // purely structural, does not require an answer
  DEFAULT = 'DEFAULT',
  SHORT_TEXT = 'SHORT_TEXT',
  ESSAY = 'ESSAY',
  DIAGRAM = 'DIAGRAM',
  SKETCH = 'SKETCH',
  GAP_TEXT = 'GAP_TEXT',
  LABEL_IMAGE = 'LABEL_IMAGE',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  IMAGE_ANALYSIS = 'IMAGE_ANALYSIS',
  MAPPING = 'MAPPING',
  LEGAL_CASE = 'LEGAL_CASE',
  CALCULATION = 'CALCULATION',
  MATHEMATICAL_PROOF = 'MATHEMATICAL_PROOF',
  CURVE_ANALYSIS = 'CURVE_ANALYSIS',
  PLOTTING = 'PLOTTING',
  GEOMETRY = 'GEOMETRY',
  PROGRAMMING = 'PROGRAMMING',
  ART = 'ART',
  DICTATION = 'DICTATION',
  TRANSLATION = 'TRANSLATION',
  CHEMICAL_FORMULA = 'CHEMICAL_FORMULA',
  MUSIC = 'MUSIC',
  LITERARY_ANALYSIS_POETRY = 'LITERARY_ANALYSIS_POETRY',
  ANALYSIS_NON_FICTION = 'ANALYSIS_NON_FICTION',
  MATERIAL_BASED_ESSAY = 'MATERIAL_BASED_ESSAY',
}

const taskTypeLabelKeys: Record<TaskType, string> = {
  [TaskType.TASK_FRAME]: 'TaskTypes.taskFrame',
  [TaskType.DEFAULT]: 'TaskTypes.default',
  [TaskType.SHORT_TEXT]: 'TaskTypes.shortText',
  [TaskType.ESSAY]: 'TaskTypes.essay',
  [TaskType.DIAGRAM]: 'TaskTypes.diagram',
  [TaskType.SKETCH]: 'TaskTypes.sketch',
  [TaskType.GAP_TEXT]: 'TaskTypes.gapText',
  [TaskType.LABEL_IMAGE]: 'TaskTypes.labelImage',
  [TaskType.MULTIPLE_CHOICE]: 'TaskTypes.multipleChoice',
  [TaskType.IMAGE_ANALYSIS]: 'TaskTypes.imageAnalysis',
  [TaskType.MAPPING]: 'TaskTypes.mapping',
  [TaskType.LEGAL_CASE]: 'TaskTypes.legalCase',
  [TaskType.CALCULATION]: 'TaskTypes.calculation',
  [TaskType.MATHEMATICAL_PROOF]: 'TaskTypes.mathematicalProof',
  [TaskType.CURVE_ANALYSIS]: 'TaskTypes.curveAnalysis',
  [TaskType.PLOTTING]: 'TaskTypes.plotting',
  [TaskType.GEOMETRY]: 'TaskTypes.geometry',
  [TaskType.PROGRAMMING]: 'TaskTypes.programming',
  [TaskType.ART]: 'TaskTypes.art',
  [TaskType.DICTATION]: 'TaskTypes.dictation',
  [TaskType.TRANSLATION]: 'TaskTypes.translation',
  [TaskType.CHEMICAL_FORMULA]: 'TaskTypes.chemicalFormula',
  [TaskType.MUSIC]: 'TaskTypes.music',
  [TaskType.LITERARY_ANALYSIS_POETRY]: 'TaskTypes.literaryAnalysisPoetry',
  [TaskType.ANALYSIS_NON_FICTION]: 'TaskTypes.analysisNonFiction',
  [TaskType.MATERIAL_BASED_ESSAY]: 'TaskTypes.materialBasedEssay',
};

const taskTypeLabelFallbacks: Record<TaskType, string> = {
  [TaskType.TASK_FRAME]: 'Task Frame',
  [TaskType.DEFAULT]: 'Default',
  [TaskType.SHORT_TEXT]: 'Short text',
  [TaskType.ESSAY]: 'Essay',
  [TaskType.DIAGRAM]: 'Diagram',
  [TaskType.SKETCH]: 'Sketch',
  [TaskType.GAP_TEXT]: 'Gap text',
  [TaskType.LABEL_IMAGE]: 'Label image',
  [TaskType.MULTIPLE_CHOICE]: 'Multiple choice',
  [TaskType.IMAGE_ANALYSIS]: 'Image analysis',
  [TaskType.MAPPING]: 'Mapping',
  [TaskType.LEGAL_CASE]: 'Legal case',
  [TaskType.CALCULATION]: 'Calculation',
  [TaskType.MATHEMATICAL_PROOF]: 'Mathematical proof',
  [TaskType.CURVE_ANALYSIS]: 'Curve analysis',
  [TaskType.PLOTTING]: 'Plotting',
  [TaskType.GEOMETRY]: 'Geometry',
  [TaskType.PROGRAMMING]: 'Programming',
  [TaskType.ART]: 'Art',
  [TaskType.DICTATION]: 'Dictation',
  [TaskType.TRANSLATION]: 'Translation',
  [TaskType.CHEMICAL_FORMULA]: 'Chemical formula',
  [TaskType.MUSIC]: 'Music',
  [TaskType.LITERARY_ANALYSIS_POETRY]: 'Poetry analysis',
  [TaskType.ANALYSIS_NON_FICTION]: 'Non-fiction analysis',
  [TaskType.MATERIAL_BASED_ESSAY]: 'Material-based essay',
};

const taskTypeTooltipKeys: Record<TaskType, string> = {
  [TaskType.TASK_FRAME]: 'TaskTypeDescs.taskFrame',
  [TaskType.DEFAULT]: 'TaskTypeDescs.default',
  [TaskType.SHORT_TEXT]: 'TaskTypeDescs.shortText',
  [TaskType.ESSAY]: 'TaskTypeDescs.essay',
  [TaskType.DIAGRAM]: 'TaskTypeDescs.diagram',
  [TaskType.SKETCH]: 'TaskTypeDescs.sketch',
  [TaskType.GAP_TEXT]: 'TaskTypeDescs.gapText',
  [TaskType.LABEL_IMAGE]: 'TaskTypeDescs.labelImage',
  [TaskType.MULTIPLE_CHOICE]: 'TaskTypeDescs.multipleChoice',
  [TaskType.IMAGE_ANALYSIS]: 'TaskTypeDescs.imageAnalysis',
  [TaskType.MAPPING]: 'TaskTypeDescs.mapping',
  [TaskType.LEGAL_CASE]: 'TaskTypeDescs.legalCase',
  [TaskType.CALCULATION]: 'TaskTypeDescs.calculation',
  [TaskType.MATHEMATICAL_PROOF]: 'TaskTypeDescs.mathematicalProof',
  [TaskType.CURVE_ANALYSIS]: 'TaskTypeDescs.curveAnalysis',
  [TaskType.PLOTTING]: 'TaskTypeDescs.plotting',
  [TaskType.GEOMETRY]: 'TaskTypeDescs.geometry',
  [TaskType.PROGRAMMING]: 'TaskTypeDescs.programming',
  [TaskType.ART]: 'TaskTypeDescs.art',
  [TaskType.DICTATION]: 'TaskTypeDescs.dictation',
  [TaskType.TRANSLATION]: 'TaskTypeDescs.translation',
  [TaskType.CHEMICAL_FORMULA]: 'TaskTypeDescs.chemicalFormula',
  [TaskType.MUSIC]: 'TaskTypeDescs.music',
  [TaskType.LITERARY_ANALYSIS_POETRY]: 'TaskTypeDescs.literaryAnalysisPoetry',
  [TaskType.ANALYSIS_NON_FICTION]: 'TaskTypeDescs.analysisNonFiction',
  [TaskType.MATERIAL_BASED_ESSAY]: 'TaskTypeDescs.materialBasedEssay',
};

const taskTypeTooltipFallbacks: Record<TaskType, string> = {
  [TaskType.TASK_FRAME]: 'Groups subtasks; no direct student answer',
  [TaskType.DEFAULT]: 'General task type when no other fits',
  [TaskType.SHORT_TEXT]: 'Short written answer (a few sentences)',
  [TaskType.ESSAY]: 'Long structured text, e.g. an essay',
  [TaskType.DIAGRAM]: 'Technical drawing, e.g. flowchart or circuit',
  [TaskType.SKETCH]: 'Freehand drawing or illustration',
  [TaskType.GAP_TEXT]: 'Fill in the blanks (cloze test)',
  [TaskType.LABEL_IMAGE]: 'Label parts of an image or diagram',
  [TaskType.MULTIPLE_CHOICE]: 'Select from given options',
  [TaskType.IMAGE_ANALYSIS]: 'Describe or analyze a provided image',
  [TaskType.MAPPING]: 'Connect matching terms or concepts',
  [TaskType.LEGAL_CASE]: 'Legal case analysis (Gutachtenstil)',
  [TaskType.CALCULATION]: 'Math or physics calculations',
  [TaskType.MATHEMATICAL_PROOF]: 'Formal mathematical proof',
  [TaskType.CURVE_ANALYSIS]: 'Function analysis (roots, extrema, …)',
  [TaskType.PLOTTING]: 'Draw or plot a graph or function',
  [TaskType.GEOMETRY]: 'Geometric constructions or calculations',
  [TaskType.PROGRAMMING]: 'Write or analyze source code',
  [TaskType.ART]: 'Create artwork or artistic design',
  [TaskType.DICTATION]: 'Write down dictated text',
  [TaskType.TRANSLATION]: 'Translate text to another language',
  [TaskType.CHEMICAL_FORMULA]: 'Write or balance chemical equations',
  [TaskType.MUSIC]: 'Read or write musical notation',
  [TaskType.LITERARY_ANALYSIS_POETRY]: 'Analyse a poem',
  [TaskType.ANALYSIS_NON_FICTION]: 'Analyse a non-fiction text',
  [TaskType.MATERIAL_BASED_ESSAY]: 'Write an essay based on provided materials',
};

export function getTaskTypeTooltip(
  taskType: TaskType | string | null | undefined,
  translate?: (key: string, options?: { defaultValue?: string }) => string,
): string {
  const normalizedTaskType = Object.values(TaskType).includes(taskType as TaskType)
    ? (taskType as TaskType)
    : TaskType.DEFAULT;
  const key = taskTypeTooltipKeys[normalizedTaskType];
  const fallback = taskTypeTooltipFallbacks[normalizedTaskType];

  if (typeof translate === 'function') {
    return translate(key, { defaultValue: fallback });
  }

  return fallback;
}

export function getTaskTypeLabel(
  taskType: TaskType | string | null | undefined,
  translate?: (key: string, options?: { defaultValue?: string }) => string,
): string {
  const normalizedTaskType = Object.values(TaskType).includes(taskType as TaskType)
    ? (taskType as TaskType)
    : TaskType.DEFAULT;
  const key = taskTypeLabelKeys[normalizedTaskType];
  const fallback = taskTypeLabelFallbacks[normalizedTaskType];

  if (typeof translate === 'function') {
    return translate(key, { defaultValue: fallback });
  }

  return fallback;
}

export const TaskTypeDescription = {
  [TaskType.TASK_FRAME]: 'for parent tasks that serve as grouping, instructions, or context for subtasks',
  [TaskType.DEFAULT]:
    'if the task fits no other taskType or if the taskType cannot be determined based on the available information',
  [TaskType.SHORT_TEXT]:
    'if the task requires a short to medium textual answer (sentences or brief paragraphs) without complex formatting',
  [TaskType.ESSAY]:
    'if the task requires writing a longer, structured continuous text, such as an essay, article, or detailed textual analysis and cannot be classified as one of the more specific task types Literary Analysis, Non-fiction Analysis or Material-based essay.',
  [TaskType.LITERARY_ANALYSIS_POETRY]:
    'If the task requires analyzing a poem, focusing on the connection between formal structures (meter, rhyme scheme, stanzas) and their thematic and stylistic interpretation.',
  [TaskType.ANALYSIS_NON_FICTION]:
    "If the task requires analyzing pragmatic, informational, or non-fictional texts (e.g., political speeches, essays, newspaper articles), focusing on the author's argument structure, rhetorical devices, and persuasive intention.",
  [TaskType.MATERIAL_BASED_ESSAY]:
    'If the task requires writing a highly structured text (e.g., a formal debate, pro/con essay, or linear discussion) based on multiple provided source materials like newspaper articles, graphics, or data sets, where the student must synthesize information from these materials to develop an independent argument or perspective.',
  [TaskType.DIAGRAM]:
    'if the task requires structured data or concept visualization that is not strictly mathematical but also not purely a freehand sketch',
  [TaskType.SKETCH]: 'if the task requires a freehand drawing, sketch, or visual illustration',
  [TaskType.GAP_TEXT]:
    'if the task involves filling in missing words or phrases in a provided text with blank lines (cloze test)',
  [TaskType.LABEL_IMAGE]:
    'if the task requires adding labels, names, or annotations to specific parts of a provided image, map, or diagram',
  [TaskType.MULTIPLE_CHOICE]:
    'if the task is a multiple-choice question with clear options (e.g. a), b), c) or checkBoxes etc.)',
  [TaskType.IMAGE_ANALYSIS]:
    'if the task requires analyzing, interpreting, or describing a provided image, painting, or photograph',
  [TaskType.MAPPING]:
    'if the task requires mapping, connecting, or matching elements together (e.g. drawing lines between terms and definitions)',
  [TaskType.LEGAL_CASE]:
    'if the task involves analyzing a legal case study, applying statutes, or writing a legal opinion (e.g., Gutachtenstil)',
  [TaskType.CALCULATION]: 'if the task requires standard mathematical or physical calculations and solving equations',
  [TaskType.MATHEMATICAL_PROOF]:
    'if the task explicitly requires formulating a formal mathematical logic proof or induction. For tasks that involve calculations but do not explicitly require a formal proof, use CALCULATION instead.',
  [TaskType.CURVE_ANALYSIS]:
    'if the task involves the calculus-based analysis of a mathematical function (Kurvendiskussion), finding roots, extrema, or inflection points',
  [TaskType.PLOTTING]:
    'if the task requires drawing or plotting a mathematical function, graph, or data points into a coordinate system',
  [TaskType.GEOMETRY]:
    'if the task involves geometric constructions, drawing shapes, measuring angles, or calculating spatial dimensions',
  [TaskType.PROGRAMMING]:
    'if the task requires writing, analyzing, or completing source code, pseudocode, or algorithms',
  [TaskType.ART]:
    'if the task involves creating a piece of artwork, painting, or complex artistic design (typically in art class)',
  [TaskType.DICTATION]:
    'if the task involves writing down text that is dictated verbally (typically recognizable by blank lines without a specific textual prompt)',
  [TaskType.TRANSLATION]: 'if the task requires translating text or single words from one language to another',
  [TaskType.CHEMICAL_FORMULA]:
    'if the task requires writing, balancing, or completing chemical equations, reaction mechanisms, Lewis structures, or structural formulas',
  [TaskType.MUSIC]:
    'if the task requires reading, writing, or analyzing musical notation, sheet music, intervals, or chords',
};

type BbxKind = (typeof bbxKind)[keyof typeof bbxKind];

export type TaskTypeBehaviour = {
  implemented: boolean; // whether the task type is fully implemented in the system (used for feature flagging and UI hints)
  structureAgentPrompt: string; // if non-empty, it will be loaded into the EXTRACTION structure agent (an uploaded/scanned exam's page images — see prompts.ts) as system prompt when handling tasks of this type
  examCreationPrompt: string; // if non-empty, it will be loaded into the exam-CREATION agent's task-type catalogue (see buildTaskTypeCatalogue in exam-creation-agent.ts) — a model writing typst source from scratch, never shown scanned pages. Separate from structureAgentPrompt because that agent needs typst/widget-usage guidance an extraction agent has no use for (it never touches typst), while shared structural rules (e.g. "combine multi-part essays into one task") belong in both.
  // if the following fields are non-empty strings, they will be loaded into the respective agents as system prompts,
  // if set to the default empty string '', it will be ignored when building the agent prompt
  boundingBox: Record<BbxKind, string>; // prompt for bbx agent depending on bbxKind
  OCR: Record<
    | OcrExtractionType.STUDENT_ANSWER // prompt for OCR extraction of student answer
    | OcrExtractionType.TASK_DESCRIPTION // prompt for OCR extraction of task description
    | OcrExtractionType.MASTER_SOLUTION // prompt for OCR extraction of master solution
    | OcrExtractionType.GRADING_CRITERIA, // prompt for OCR extraction of grading criteria
    string
  >;
  generateMS: string; // prompt for generating master solution
  generateGS: string; // prompt for generating gradingScheme
  grading: string; // prompt for grading
  // Grammar-eligible task types only (see shared/grammar-grading.ts GRAMMAR_TASK_TYPES): the fraction of a
  // node's declared total points allocated to grammar when AI-generating a grading scheme. Plain data, not
  // an LLM prompt fragment — not run through the header-injection loop below. The static grammar criteria
  // TEXT itself (used verbatim as a solo LanguageUseScheme's criteria for a standalone node) lives in
  // localization/messages/*.json under "LanguageUseTemplates" instead, keyed by task type — it's user-facing
  // content shown in the grading canvas, so it must match the exam's own language, not a single hardcoded one.
  grammarPointsRatio?: number;
  // When true, student answer bounding boxes skip OCR entirely and are stored as contentType 'image'.
  // Use for task types where handwritten content (e.g. math notation, graphs, formulas) is not reliably
  // transcribable by OCR. The cropped image region is still saved for downstream LLM processing.
  // This flag only affects student answer extraction — task description, master solution, and grading
  // criteria OCR are unaffected.
  forceImage?: boolean;
};

const taskTypeBehavioursRaw: Record<TaskType, TaskTypeBehaviour> = {
  [TaskType.TASK_FRAME]: taskFrameBehaviour,
  [TaskType.DEFAULT]: defaultBehaviour,
  [TaskType.SHORT_TEXT]: textBehaviour,
  [TaskType.ESSAY]: essayBehaviour,
  [TaskType.DIAGRAM]: diagramBehaviour,
  [TaskType.SKETCH]: sketchBehaviour,
  [TaskType.GAP_TEXT]: gapTextBehaviour,
  [TaskType.LABEL_IMAGE]: labelImageBehaviour,
  [TaskType.MULTIPLE_CHOICE]: multipleChoiceBehaviour,
  [TaskType.IMAGE_ANALYSIS]: imageAnalysisBehaviour,
  [TaskType.MAPPING]: mappingBehaviour,
  [TaskType.LEGAL_CASE]: legalCaseBehaviour,
  [TaskType.CALCULATION]: calculationBehaviour,
  [TaskType.MATHEMATICAL_PROOF]: mathematicalProofBehaviour,
  [TaskType.CURVE_ANALYSIS]: curveAnalysisBehaviour,
  [TaskType.PLOTTING]: plottingBehaviour,
  [TaskType.GEOMETRY]: geometryBehaviour,
  [TaskType.PROGRAMMING]: programmingBehaviour,
  [TaskType.ART]: artBehaviour,
  [TaskType.DICTATION]: dictationBehaviour,
  [TaskType.TRANSLATION]: translationBehaviour,
  [TaskType.CHEMICAL_FORMULA]: chemicalFormulaBehaviour,
  [TaskType.MUSIC]: musicBehaviour,
  [TaskType.LITERARY_ANALYSIS_POETRY]: poetryAnalysisBehaviour,
  [TaskType.ANALYSIS_NON_FICTION]: nonFictionBehaviour,
  [TaskType.MATERIAL_BASED_ESSAY]: materialBasedEssayBehaviour,
};

// add a introducing sentence to the prompt based on if it is a single task or multiple task are handled at once
function getFieldIntroHeader(taskType: string, mode: 'single' | 'multiple') {
  return mode === 'single' ? `Since this is a "${taskType}" task:` : `For tasks with taskType="${taskType}":`;
}

function injectFieldIntro(taskType: string, fieldKey: string, content: string, mode: 'single' | 'multiple') {
  // Only inject header when original content is non-empty — otherwise leave empty.
  if (!content || String(content).trim() === '') return content;
  const header = getFieldIntroHeader(taskType, mode);
  return `${header}\n\n${content}`;
}

function injectSubFieldIntro(
  taskType: string,
  fieldKey: string,
  subKey: string,
  content: string,
  mode: 'single' | 'multiple',
) {
  // Only inject header when original content is non-empty — otherwise leave empty.
  if (!content || String(content).trim() === '') return content;
  const header = getFieldIntroHeader(taskType, mode);
  return `${header}\n\n${content}`;
}

// Inject default introductions into each behaviour so callsites don't change.
Object.keys(taskTypeBehavioursRaw).forEach(k => {
  const key = k as TaskType;
  const b = taskTypeBehavioursRaw[key] as unknown as Record<string, any>;
  // simple fields — MS/GS generation now runs over a whole task subtree (multiple task types injected
  // together), like grading, so all three use the "multiple" (taskType-scoped) header.
  ['generateMS', 'generateGS', 'grading'].forEach(f => {
    if (f in b) b[f] = injectFieldIntro(key, f, b[f] ?? '', 'multiple');
  });
  // OCR subfields
  if (b.OCR && typeof b.OCR === 'object') {
    Object.keys(b.OCR).forEach(sk => {
      b.OCR[sk] = injectSubFieldIntro(key, 'OCR', sk, b.OCR[sk] ?? '', 'single');
    });
  }
  // boundingBox subfields
  if (b.boundingBox && typeof b.boundingBox === 'object') {
    Object.keys(b.boundingBox).forEach(sk => {
      b.boundingBox[sk] = injectSubFieldIntro(key, 'boundingBox', sk, b.boundingBox[sk] ?? '', 'multiple');
    });
  }
});

// Export final behaviours after injection — keep the exported binding immutable from reassignment perspective
export const taskTypeBehaviours: Record<TaskType, TaskTypeBehaviour> = taskTypeBehavioursRaw;
